import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Menu, X, Moon, Sun } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { cn } from "../../lib/utils";
import { MarketingCopy, MarketingLanguage, languageOptions } from "../../content/marketingCopy";
import { getMarketingUi } from "../../content/marketingUi";
import { BrandLogo } from "../BrandLogo";
import { getSalesDoc, SALES_DOC_PATHS, type SalesDocId } from "../../content/salesTerms";
import { CookieConsentBanner } from "../CookieConsentBanner";

interface MarketingLayoutProps {
  copy: MarketingCopy;
  language: MarketingLanguage;
  setLanguage: (lang: MarketingLanguage) => void;
  children: ReactNode;
}

const LANGUAGE_FLAGS: Record<MarketingLanguage, string> = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ru: "🇷🇺",
  de: "🇩🇪",
  fr: "🇫🇷",
  it: "🇮🇹"
};

/**
 * Language picker. Opens as a dropdown below the button (popper) so all six
 * languages are visible at once — the default item-aligned mode laid the list
 * over the header and clipped it, with no visible scroll. The chosen language
 * is shown with its flag on the button and highlighted in the list.
 */
function LanguageSelect({
  language,
  setLanguage,
  label,
  wide = false
}: {
  language: MarketingLanguage;
  setLanguage: (lang: MarketingLanguage) => void;
  label: string;
  wide?: boolean;
}) {
  const current = languageOptions.find((o) => o.value === language);
  return (
    <Select value={language} onValueChange={(value) => setLanguage(value as MarketingLanguage)}>
      <SelectTrigger
        className={cn(
          "shrink-0 gap-2 border-border bg-card font-medium text-foreground",
          wide ? "h-10 w-[150px] text-sm" : "h-9 w-auto px-2.5 text-sm sm:w-[136px]"
        )}
        aria-label={label}
      >
        <span className="flex items-center gap-2">
          <span className="text-base leading-none" aria-hidden="true">
            {LANGUAGE_FLAGS[language]}
          </span>
          <span className={cn(!wide && "hidden sm:inline")}>{current?.label}</span>
          {!wide && <span className="uppercase sm:hidden">{language}</span>}
        </span>
      </SelectTrigger>
      <SelectContent position="popper" sideOffset={6} align="end" className="w-52 p-1">
        {languageOptions.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            className="cursor-pointer py-2.5 text-[15px] data-[state=checked]:bg-primary/10 data-[state=checked]:font-semibold data-[state=checked]:text-primary"
          >
            <span className="mr-2" aria-hidden="true">
              {LANGUAGE_FLAGS[option.value]}
            </span>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const MarketingLayout = ({ copy, language, setLanguage, children }: MarketingLayoutProps) => {
  const isTr = language === "tr";
  const ui = getMarketingUi(language);

  // Footer "Cookie preferences" bumps this to reopen the banner.
  const [cookieBannerSignal, setCookieBannerSignal] = useState(0);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDark, setIsDark] = useState(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("dark")
  );
  const toggleTheme = () => {
    const next = !isDark;
    document.documentElement.classList.toggle("dark", next);
    setIsDark(next);
  };

  const navLinks = [
    { to: "/#urun", label: ui.nav.product },
    { to: "/#nasil-calisir", label: ui.nav.howItWorks },
    { to: "/pricing", label: ui.nav.pricing },
    { to: "/randevu", label: ui.nav.appointments },
    { to: "/sectors/kuafor", label: ui.nav.industries },
    { to: "/help-center", label: copy.nav.helpCenter }
  ];

  const demoHref = `mailto:info@meethumo.com?subject=${encodeURIComponent(ui.demoSubject)}`;
  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

  return (
    <div className="marketing-theme fixed inset-0 overflow-y-auto overflow-x-hidden bg-background text-foreground">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-foreground focus:px-3 focus:py-2 focus:text-sm focus:text-background"
      >
        {ui.skipToContent}
      </a>
      <header className="fixed left-0 right-0 top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <Link to="/" className={`flex shrink-0 items-center rounded-md text-foreground ${focusRing}`} aria-label="Hümo">
            <BrandLogo markSize={30} showWordmark wordmarkClassName="text-xl max-[399px]:hidden" />
          </Link>

          <nav aria-label={ui.mainNav} className="hidden items-center gap-7 lg:flex">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-sm text-sm font-medium text-muted-foreground transition-colors hover:text-foreground ${focusRing}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {/* Language select, visible at every width (it used to be xl-only,
                leaving 1024–1279px screens with no way to switch). */}
            <LanguageSelect language={language} setLanguage={setLanguage} label={ui.language} />
            <Link
              to="/login"
              className={`hidden rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground md:inline-flex ${focusRing}`}
            >
              {ui.login}
            </Link>
            <a
              href={demoHref}
              className={`hidden h-9 items-center rounded-lg border border-border px-3.5 text-sm font-medium text-foreground transition-colors hover:bg-muted md:inline-flex ${focusRing}`}
            >
              {ui.bookDemo}
            </a>
            <Link
              to="/signup?next=/onboarding"
              className={`inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:px-3.5 ${focusRing}`}
            >
              {ui.startFree}
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-foreground lg:hidden ${focusRing}`}
              aria-expanded={mobileOpen}
              aria-controls="marketing-mobile-menu"
              aria-label={mobileOpen ? ui.closeMenu : ui.openMenu}
            >
              {mobileOpen ? <X className="h-4 w-4" aria-hidden="true" /> : <Menu className="h-4 w-4" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div id="marketing-mobile-menu" className="border-t border-border bg-background px-4 pb-5 pt-2 lg:hidden">
            <nav aria-label={ui.mobileNav} className="flex flex-col">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={`border-b border-border py-3 text-base font-medium text-foreground ${focusRing}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className={`border-b border-border py-3 text-base font-medium text-foreground ${focusRing}`}
              >
                {ui.login}
              </Link>
            </nav>
            <div className="mt-4 flex items-center gap-3">
              <a
                href={demoHref}
                className={`inline-flex h-10 flex-1 items-center justify-center rounded-lg border border-border text-sm font-medium text-foreground ${focusRing}`}
              >
                {ui.bookDemo}
              </a>
              <LanguageSelect language={language} setLanguage={setLanguage} label={ui.language} wide />
            </div>
          </div>
        )}
      </header>

      <main id="main" className="pt-16">{children}</main>

      <footer className="bg-gray-950 py-12 text-gray-300">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid gap-8 md:grid-cols-5 md:gap-10">
            <div className="md:col-span-2">
              <div className="mb-4 flex items-center text-white">
                <BrandLogo markSize={28} showWordmark wordmarkClassName="text-xl" />
              </div>
              <p className="text-sm text-gray-400">{copy.footer.tagline}</p>

              {/* Social links: only accounts that exist. LinkedIn is the
                  company page (TUBU ARGE); Hümo has no X account. */}
              <div className="mt-5 flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/company/tubu-arge/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-white"
                >
                  <Linkedin className="h-4 w-4" aria-hidden="true" />
                </a>
                <a
                  href="https://www.instagram.com/meethumo/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-white"
                >
                  <Instagram className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </div>

            <div>
              <h2 className="mb-4 text-sm font-semibold text-white">{copy.footer.product}</h2>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/features" className="transition-colors hover:text-white">
                    {copy.footer.links.features}
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="transition-colors hover:text-white">
                    {copy.footer.links.pricing}
                  </Link>
                </li>
                <li>
                  <Link to="/sectors/kuafor" className="transition-colors hover:text-white">
                    {ui.sectors.salon}
                  </Link>
                </li>
                <li>
                  <Link to="/sectors/marina" className="transition-colors hover:text-white">
                    {ui.sectors.marina}
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="transition-colors hover:text-white">
                    {copy.footer.links.blog}
                  </Link>
                </li>
                <li>
                  <Link to="/help-center" className="transition-colors hover:text-white">
                    {copy.footer.links.helpCenter}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="mb-4 text-sm font-semibold text-white">{copy.footer.company}</h2>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/about" className="transition-colors hover:text-white">
                    {copy.footer.links.about}
                  </Link>
                </li>
                <li>
                  <Link to="/careers" className="transition-colors hover:text-white">
                    {copy.footer.links.careers}
                  </Link>
                </li>
                <li>
                  <Link to="/guvenlik" className="transition-colors hover:text-white">
                    {isTr ? "Güvenlik ve kendi sunucunuzda" : "Security and on-premises"}
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="transition-colors hover:text-white">
                    {ui.privacyPolicy}
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="transition-colors hover:text-white">
                    {ui.terms}
                  </Link>
                </li>
                {isTr && (
                  <li>
                    <Link to="/privacy-policy#kvkk" className="transition-colors hover:text-white">
                      KVKK Aydınlatma Metni
                    </Link>
                  </li>
                )}
                {(Object.keys(SALES_DOC_PATHS) as SalesDocId[]).map((id) => (
                  <li key={id}>
                    <Link to={SALES_DOC_PATHS[id]} className="transition-colors hover:text-white">
                      {getSalesDoc(id, language).title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="mb-4 text-sm font-semibold text-white">{copy.footer.support}</h2>
              <ul className="space-y-2 text-sm">
                <li>
                  <span className="block text-xs uppercase tracking-wide text-gray-400">
                    {ui.general}
                  </span>
                  <a href="mailto:support@meethumo.com" className="transition-colors hover:text-white">
                    support@meethumo.com
                  </a>
                </li>
                <li>
                  <span className="block text-xs uppercase tracking-wide text-gray-400">
                    {ui.sales}
                  </span>
                  <a href="mailto:info@meethumo.com" className="transition-colors hover:text-white">
                    info@meethumo.com
                  </a>
                </li>
                <li>
                  <span className="block text-xs uppercase tracking-wide text-gray-400">KVKK / DPO</span>
                  <a href="mailto:kvkk@meethumo.com" className="transition-colors hover:text-white">
                    kvkk@meethumo.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Legal + company info row (marketingUi companyLine). E-ticaret
              hizmet sağlayıcı yönetmeliği md. 5: ticaret unvanı, MERSİS no,
              merkez adresi, telefon, e-posta ve KEP adresi sitede bulunmalı. */}
          {/* Card schemes accepted at checkout; payment institutions ask for
              these on the site before opening a virtual POS. */}
          <div className="mt-10 flex flex-wrap items-center gap-3 text-xs text-gray-400">
            <img src="/payment/visa.png" alt="Visa" className="h-6 w-auto rounded bg-white px-1.5 py-0.5" />
            <img src="/payment/mastercard.png" alt="Mastercard" className="h-6 w-auto rounded bg-white px-1.5 py-0.5" />
            {/* Paratika asks merchants to show its logo, linked to its site, so
                buyers know payments run on a licensed payment institution. */}
            <a href="https://www.paratika.com.tr" target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0">
              <img src="/payment/paratika.png" alt="Paratika Sanal POS" className="h-7 w-auto" />
            </a>
            <span>{isTr ? "3D Secure ile güvenli ödeme · Paratika altyapısı" : "Secure 3D Secure payments · powered by Paratika"}</span>
          </div>

          <div className="mt-6 border-t border-gray-800 pt-6">
            <div className="flex flex-col items-start gap-3 text-xs text-gray-400 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-gray-400">{copy.footer.rights}</p>
                <p className="mt-2 flex items-center gap-2">
                  <a href="https://tubu.io" target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 rounded-sm">
                    <img src="/brand/tubu.svg" alt="Tubu" className="h-4 w-auto" />
                  </a>
                  <span>{ui.companyLine}</span>
                </p>
              </div>
              <nav className="flex flex-wrap items-center gap-4">
                <Link to="/privacy-policy" className="transition-colors hover:text-gray-300">
                  {ui.privacyShort}
                </Link>
                <span className="text-gray-700">·</span>
                <Link to="/terms-of-service" className="transition-colors hover:text-gray-300">
                  {ui.termsShort}
                </Link>
                <span className="text-gray-700">·</span>
                <button
                  type="button"
                  onClick={() => setCookieBannerSignal((n) => n + 1)}
                  className="transition-colors hover:text-gray-300"
                >
                  {ui.cookiePreferences}
                </button>
                <span className="text-gray-700">·</span>
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-gray-300"
                  aria-pressed={isDark}
                >
                  {isDark ? <Sun className="h-3.5 w-3.5" aria-hidden="true" /> : <Moon className="h-3.5 w-3.5" aria-hidden="true" />}
                  {isDark ? ui.lightTheme : ui.darkTheme}
                </button>
              </nav>
            </div>
          </div>
        </div>
      </footer>

      <CookieConsentBanner language={language} reopenSignal={cookieBannerSignal} />
    </div>
  );
};

export default MarketingLayout;
